import { useState } from "react";
import { Link } from "react-router-dom";

import { useDebouncedValue } from "../lib/hooks";
import { trpc } from "../trpc";

interface DohaRef {
  slug: string;
  mandal: number;
  chapter: number;
  doha: number;
}

/**
 * Shows which doha a capture is tagged with, and (for the owner) lets it
 * be changed or cleared after the fact — search a new M/C/D reference,
 * or untag entirely.
 */
export function DohaTagEditor({
  captureId,
  doha,
  canEdit,
}: {
  captureId: number;
  doha: DohaRef | null;
  canEdit: boolean;
}) {
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query.trim());
  const results = trpc.dohas.search.useQuery(
    { q: debounced, limit: 12 },
    { enabled: editing && debounced.length > 0 },
  );
  const setDoha = trpc.captures.setDoha.useMutation({
    onSuccess: async () => {
      setEditing(false);
      setQuery("");
      await utils.captures.get.invalidate({ id: captureId });
      await utils.captures.mine.invalidate();
    },
  });

  if (!editing) {
    return (
      <div>
        {doha ? (
          <Link
            to={`/doha/${doha.slug}`}
            className="font-serif text-2xl text-ink transition-colors hover:text-terracotta"
          >
            Mandal {doha.mandal} · Chapter {doha.chapter} · Doha {doha.doha}
          </Link>
        ) : (
          <p className="font-serif text-2xl text-ink-soft">Untagged</p>
        )}
        {canEdit ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="eyebrow mt-1.5 block transition-colors hover:text-terracotta"
          >
            {doha ? "Change doha" : "Tag this doha"}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <input
        type="search"
        value={query}
        autoFocus
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search a reference — e.g. M2C5D3"
        className="w-full rounded-xl border border-line-strong bg-paper-raised px-4 py-2 font-mono text-sm text-ink outline-none focus:border-terracotta/60"
        aria-label="Search a doha reference"
      />
      {results.data && results.data.length > 0 ? (
        <ul className="mt-1.5 max-h-56 overflow-auto rounded-xl border border-line bg-paper-raised">
          {results.data.map((r) => (
            <li key={r.slug}>
              <button
                type="button"
                onClick={() =>
                  setDoha.mutate({ id: captureId, dohaSlug: r.slug })
                }
                className="block w-full px-4 py-2 text-left font-mono text-sm text-ink transition-colors hover:bg-paper-sunk"
              >
                {r.slug}
                <span className="ml-2 font-sans text-xs text-ink-faint">
                  Mandal {r.mandal} · Chapter {r.chapter} · Doha {r.doha}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-2 flex items-center gap-4 text-xs">
        {doha ? (
          <button
            type="button"
            onClick={() => setDoha.mutate({ id: captureId, dohaSlug: null })}
            disabled={setDoha.isPending}
            className="text-ink-faint transition-colors hover:text-terracotta disabled:opacity-50"
          >
            Untag
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setQuery("");
          }}
          className="text-ink-faint transition-colors hover:text-ink"
        >
          Cancel
        </button>
        {setDoha.isPending ? (
          <span className="text-ink-faint">Saving…</span>
        ) : null}
      </div>
    </div>
  );
}
